import type { BoxPageHeading } from "@loot-os/design-system/templates/box/boxPageContent";
import { LootBoxOpener } from "@loot-os/react";
import { analyticsErrorCode, track } from "../lib/analytics.js";
import { useCurrency } from "../lib/currency.js";
import { invalidateAfterClaim, invalidateAfterOpen } from "../lib/queries.js";
import { pendingRevealFromError, useRevealClaim } from "../lib/revealClaim.js";
import { useShopper } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import { BoxIdleHero } from "./BoxIdleHero.js";

/**
 * The box page's opener slot: `@loot-os/react`'s embeddable opener, wired to the app. Signed in,
 * opens persist through the Loot session token inherited from <LootProvider>; signed out the
 * opener falls back to demo mode (local rolls, nothing saved). While the auth check is still
 * running neither is known, and the opener shows its loading state (`sessionPending` on
 * <LootProvider>), so a signed-in shopper never rolls a demo open before their session is minted.
 * Before an open its idle state is the design system's BoxHero (`BoxIdleHero`, through
 * `renderIdle`); the quantity, the open, the reveal, claim and sell back stay the opener's own.
 *
 * `onRefusal` reports why an open was refused (a message), or that the last refusal no longer
 * stands (null), for the page to say above the opener.
 */
export function BoxOpener({
  boxId,
  heading,
  onRefusal,
  onHeroShown,
}: {
  boxId: string;
  /** The page's heading once the box has loaded: the hero draws its price note and meta row. */
  heading: BoxPageHeading | null;
  onRefusal: (message: string | null) => void;
  /** The idle hero is up (true) or gave way to the reveal (false). */
  onHeroShown: (shown: boolean) => void;
}) {
  const { sessionToken } = useShopper();
  const { hasFeature } = useTenant();
  const unit = useCurrency().namePlural;
  const { promptClaim } = useRevealClaim();
  return (
    <LootBoxOpener
      boxId={boxId}
      showFairness={false}
      renderIdle={(idle) => <BoxIdleHero idle={idle} heading={heading} onShown={onHeroShown} />}
      // Returns undefined: recorded, never vetoed. Signed out, the opener rolls a demo open.
      onOpenStart={(quantity) => {
        track("open_clicked", { boxId, quantity, demo: !sessionToken });
        return undefined;
      }}
      onOpened={() => {
        onRefusal(null);
        invalidateAfterOpen();
      }}
      // Reveal mode: the balance moves at claim (or sell-back), not at open.
      onClaimed={() => {
        onRefusal(null);
        invalidateAfterClaim();
      }}
      onSoldBack={() => {
        onRefusal(null);
        invalidateAfterClaim();
      }}
      onInsufficientBalance={() => {
        track("insufficient_balance", { boxId, action: "open" });
        onRefusal(
          hasFeature("balancePurchases")
            ? `Not enough ${unit} — buy more from the Account page.`
            : `Not enough ${unit}.`,
        );
      }}
      onError={(err) => {
        // Any unclaimed reveal (a prior box reveal or a battle win) blocks opening; the claim
        // modal names and claims whichever it is, then the shopper can open again.
        const hint = pendingRevealFromError(err);
        if (hint) {
          onRefusal(null);
          promptClaim(hint);
          return;
        }
        // The opener also calls onInsufficientBalance for that code, which records it.
        const code = analyticsErrorCode(err);
        if (code !== "insufficient_balance") track("open_failed", { boxId, code });
        onRefusal(err instanceof Error ? err.message : "open failed");
      }}
    />
  );
}
